import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SntRadio from "@cleanup/components/SntRadio/SntRadio";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import { Button, Container } from "react-bootstrap";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { VerticalLayout } from "@/cleanup/components/FormValidation";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterTypeSettings {
  trueLabel?: string;
  falseLabel?: string;
  naLabel?: string;
}

interface Descriptor {
  _idx: string;
  label?: string;
  description: string;
  filterTypeSettings: FilterTypeSettings;
}

interface FilterValue {
  value?: boolean | null;
  naOnly?: boolean;
  selectedValue?: string;
}

interface FilterData {
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface BooleanFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
}

interface DataState {
  selectedValue: string;
  notFilter: boolean;
}

const BooleanFilter: React.FC<BooleanFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
}) => {
  const [isShow, setShow] = useState(false);
  const [title, setTitle] = useState("");

  const [dataState, setDataState] = useState<DataState>({
    selectedValue: "",
    notFilter: false,
  });

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  useEffect(() => {
    const { filterValue } = data;
    let _title = translate(LanguageKey.all_key);
    const _dataState: DataState = {
      selectedValue: "",
      notFilter: false,
    };

    if (filterValue) {
      Object.assign(_dataState, filterValue);
    }

    if (_dataState.selectedValue === "trueLabel" || (filterValue && filterValue.value === true)) {
      _title = descriptor.filterTypeSettings.trueLabel || "Yes";
      _dataState.selectedValue = "trueLabel";
    } else if (_dataState.selectedValue === "falseLabel" || (filterValue && filterValue.value === false)) {
      _title = descriptor.filterTypeSettings.falseLabel || "No";
      _dataState.selectedValue = "falseLabel";
    } else if (filterValue && filterValue.naOnly === true) {
      _title = descriptor.filterTypeSettings.naLabel || translate(LanguageKey.na_key);
      _dataState.selectedValue = "naLabel";
    }

    setDataState({
      selectedValue: _dataState.selectedValue,
      notFilter: Boolean(data.notFilter),
    });
    setTitle(_title);
  }, [
    data,
    data.filterValue,
    descriptor.filterTypeSettings.falseLabel,
    descriptor.filterTypeSettings.trueLabel,
    descriptor.filterTypeSettings.naLabel,
  ]);

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;

    newData.filterValue = {
      value:
        dataState.selectedValue === "trueLabel"
          ? true
          : dataState.selectedValue === "falseLabel"
          ? false
          : null,
    };
    if (dataState.selectedValue === "naLabel") {
      newData.filterValue.naOnly = true;
    } else {
      delete newData.filterValue.naOnly;
    }
    newData.notFilter = dataState.notFilter;

    return newData;
  }, [data, dataState]);

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    if (
      oldDataFilter.value === newDataFilter.value &&
      oldDataFilter.naOnly === newDataFilter.naOnly &&
      (data.notFilter === newData.notFilter ||
        (data.notFilter === undefined && newData.notFilter === false) ||
        (data.notFilter === false && newData.notFilter === undefined))
    ) {
      return false;
    }
    return true;
  }, [data, getData]);

  const refWrapper = useOuterClick(() => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
  };

  const onClearFilter = () => {
    setShow(false);
    setDataState({ selectedValue: "", notFilter: false });
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
  };

  const excludeLabel = useMemo(() => {
    return data.notFilter ? <ExcludeLabel /> : null;
  }, [data.notFilter]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {dataState.selectedValue && (
          <SntCloseSmallIcon
            className="me-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearFilter();
            }}
          />
        )}
        <span>{descriptor.label || descriptor.description}</span>:{" "}
        <span>{excludeLabel}</span>
        <FilterHeaderStyle>{title}</FilterHeaderStyle>
        <SntArrowDownIcon />
      </Button>

      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Container>
          <VerticalLayout className="mt-3">
            <SntRadio
              value="trueLabel"
              selectedValue={dataState.selectedValue}
              onChange={() =>
                setDataState({ ...dataState, selectedValue: "trueLabel" })
              }
              label={
                descriptor.filterTypeSettings.trueLabel
                  ? descriptor.filterTypeSettings.trueLabel
                  : "True"
              }
            />
            <SntRadio
              value="falseLabel"
              selectedValue={dataState.selectedValue}
              onChange={() => {
                setDataState({ ...dataState, selectedValue: "falseLabel" });
              }}
              label={
                descriptor.filterTypeSettings.falseLabel
                  ? descriptor.filterTypeSettings.falseLabel
                  : "False"
              }
            />

            {descriptor.filterTypeSettings.naLabel && (
              <SntRadio
                value="naLabel"
                selectedValue={dataState.selectedValue}
                onChange={() => {
                  setDataState({
                    ...dataState,
                    selectedValue: "naLabel",
                  });
                }}
                label={
                  descriptor.filterTypeSettings.naLabel
                    ? descriptor.filterTypeSettings.naLabel
                    : translate(LanguageKey.na_key)
                }
              />
            )}
          </VerticalLayout>
        </Container>
        <GeneralFooterFilter
          onClearFilter={() => onClearFilter()}
          onAppliedFilter={() => onAppliedFilter()}
          isNotFilter={dataState.notFilter}
          onCheckNotFilter={(e) => {
            setDataState({
              ...dataState,
              notFilter: e,
            });
          }}
          disableCheckBox={!dataState.selectedValue}
        />
      </FilterBody>
    </AdvanceFilter>
  );
};

export default BooleanFilter;
